import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

import { useAttemptChanges } from '../../hooks/attempt-changes';
import { unreachable } from '../../hooks/request';

const AnswerSavesContext = createContext(null);

// The answers a patient has chosen in questionnaires, and whether each is
// saved yet (#114). Saving an answer takes a moment, and a patient can choose
// again, move to another question or leave the questionnaire before it has
// settled, so this lives above every screen rather than in one item.
//
// Each attempt's item has one entry: the latest choice, the value the service
// last acknowledged, whether a write is on its way and the last write's
// failure. Writes for an item go one at a time and always end with the latest
// choice: a choice made while a write is out waits, the choices in between
// that were never sent are dropped, and a write once sent is never taken
// back, since it may already have been applied. So an older answer can never
// land after a newer one, whatever order the network delivers them in.
//
// What the service holds is known only from its answers (#122). A write the
// service refused was not applied, so the stored value stands. A write whose
// answer never arrived, because the connection dropped or the gateway gave
// up, may well have been applied: the stored value becomes unknown, and an
// unknown value never matches the latest choice, so that choice is sent
// again, in order, even when it is the value that was stored before.
//
// A screen reads its item's entry and re-renders as it changes. The entries
// are this browser tab's only: what the service holds is still the record.
export function AnswerSavesProvider({ children }) {
  const { saveAnswer } = useAttemptChanges();
  const entries = useRef(new Map());
  const [ version, setVersion ] = useState(0);

  const changed = useCallback(function() {
    setVersion(function(current) {
      return current + 1;
    });
  }, []);

  // Sends the latest choice when it is not the acknowledged one and nothing
  // is out yet. Each settled write sends again if a newer choice arrived
  // meanwhile, and whoever waits on the item hears once it has settled.
  const pump = useCallback(function(entry) {
    if (entry.sending === true || entry.failure != null || entry.latest === entry.stored) {
      return;
    }

    const value = entry.latest;

    entry.sending = true;
    changed();

    saveAnswer({ attemptId: entry.attemptId, position: entry.position, value: Number(value) }).then(function() {
      entry.stored = value;
      entry.saves = entry.saves + 1;
    }).catch(function(failed) {
      if (failed.message === unreachable) {
        entry.stored = undefined;
      }

      // A failure belongs to the choice it tried to save: when a newer one
      // has been made since, that one is simply sent next.
      if (entry.latest === value) {
        entry.failure = failed.message;
      }
    }).then(function() {
      entry.sending = false;
      changed();
      pump(entry);

      if (entry.sending === false) {
        const waiting = entry.waiting;

        entry.waiting = [];
        waiting.forEach(function(resolve) {
          resolve();
        });
      }
    });
  }, [ saveAnswer, changed ]);

  // The entry for one item, created from what the service holds the first
  // time an item opens. A later opening keeps the entry, so a write still on
  // its way and a failure not yet resolved are not forgotten.
  const entryOf = useCallback(function({ attemptId, position, stored }) {
    const key = `${ attemptId }:${ position }`;

    if (entries.current.has(key) === false) {
      entries.current.set(key, { attemptId, position, latest: stored, stored, sending: false, failure: undefined, waiting: [], saves: 0 });
    }

    return entries.current.get(key);
  }, []);

  const choose = useCallback(function(entry, value) {
    entry.latest = value;
    entry.failure = undefined;
    changed();
    pump(entry);
  }, [ changed, pump ]);

  // Tries a failed choice again; settles with whether it is now saved.
  const settle = useCallback(function(entry) {
    entry.failure = undefined;
    changed();
    pump(entry);

    return new Promise(function(resolve) {
      if (entry.sending === false) {
        resolve();

        return;
      }

      entry.waiting.push(resolve);
    }).then(function() {
      return entry.failure == null && entry.latest === entry.stored;
    });
  }, [ changed, pump ]);

  // Settles once every write of an attempt has, with the first failure among
  // its items, if any: what submitting has to wait for.
  const settleAttempt = useCallback(function(attemptId) {
    const items = Array.from(entries.current.values()).filter(function(entry) {
      return entry.attemptId === attemptId;
    });

    return Promise.all(items.map(function(entry) {
      if (entry.sending === false) {
        return null;
      }

      return new Promise(function(resolve) {
        entry.waiting.push(resolve);
      });
    })).then(function() {
      const failed = items.find(function(entry) {
        return entry.failure != null;
      });

      if (failed == null) {
        return null;
      }

      return failed.failure;
    });
  }, []);

  // How many of an attempt's answers have been saved so far: a screen that
  // shows the attempt reads it again whenever this grows, including for an
  // item that is no longer on screen.
  const savesOf = useCallback(function(attemptId) {
    return Array.from(entries.current.values()).reduce(function(total, entry) {
      if (entry.attemptId !== attemptId) {
        return total;
      }

      return total + entry.saves;
    }, 0);
  }, []);

  const value = useMemo(function() {
    return { version, entryOf, choose, settle, settleAttempt, savesOf };
  }, [ version, entryOf, choose, settle, settleAttempt, savesOf ]);

  return <AnswerSavesContext.Provider value={ value }>{ children }</AnswerSavesContext.Provider>;
}

export function useAnswerSaves() {
  return useContext(AnswerSavesContext);
}
